import * as React from 'react';
import {EfeAccordionValues} from 'commons';
import {
    ATTR_NULL,
    Constants,
    HistoryRoutes,
    IMAGE_PREFIX, IMAGE_PREFIX_PNG,
    INE_ID,
    Messages,
    SnackbarVariants,
    ViewKeys,
} from 'commons/constants';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {MainContent} from 'layouts/commons';
import {useAlert, useSpinner} from 'hooks';
import {EfeAccordion} from 'components';
import {useHistory} from 'react-router-dom';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import RealOwnerIdentificationForm from './identification-form/RealOwnerIdentificationForm';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {Box} from '@material-ui/core';
import {
    EfeResponse,
    Field,
    IdentificationServiceRequest,
    OrcValidations,
    RealOwnerServiceRequest,
    UploadDocumentServiceRequest,
} from 'commons/models';
import {fieldCatalogGetter, useFlowFieldSearch} from 'hooks/FlowHook';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {ClientService, DocumentServices, ValidationsService} from 'services';
import {useSnackbar} from 'notistack';
import {convertBase64ToBlob, getBase64Extension, nextStep} from 'commons/utils/Functions';
import * as Validations from 'commons/models/dtos/validations/Validations';
import {getIdentification, validateOcrResponse} from 'commons/utils/DniValidationFunctions';

const errorDetail = {
    data: {
        process: Messages.IDENTIFICATION_SECTION,
        message: ' ',
        errors: [''],
    },
};

function RealOwnerIdentification() {
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const {flow, updateModuleStep} = useFlowContext();
    const {menu, kycId, expedientId, fatherKycId} = useMenuContext();
    const [fields, setFields] = React.useState<Array<Field>>(useFlowFieldSearch());
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const {tempRealOwner, setTempRealOwner, setTempRealOwnerActive} = useRelatedPeopleContext();
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [realOwnerIdentification, setRealOwnerIdentification] = React.useState<any>(undefined);
    const [realOwnerData, setRealOwnerData] = React.useState<RealOwnerServiceRequest>();

    function onChangeHandler() {
        setTempRealOwnerActive(true);
        history.push(HistoryRoutes.REAL_OWNER_IDENTIFICATION);
    }

    async function saveDni(file: string, dniName: string) {
        if (file) {
            const extension = getBase64Extension(file);
            const fileConverted = await convertBase64ToBlob(file);
            const document: UploadDocumentServiceRequest = {
                documentKey:
                    dniName === Constants.DOC_DNI_BACK_PR
                        ? Constants.DOC_DNI_BACK_PR
                        : Constants.DOC_DNI_FRONT_PR,
                expedientId: expedientId,
                file: fileConverted,
                kycId: kycId,
                filename: `${dniName}.${extension}`,
            };

            const documentResponse: EfeResponse<any> = await DocumentServices.updateDocument(
                document
            );
            return documentResponse;
        }
    }

    function goToNextView() {
        const path = nextStep(flow, menu);
        if (Constants.MODULE_STEP !== path) {
            setIsLoading(false);
            history.push(path);
        } else {
            updateModuleStep(true);
            flow.data.expandKey = path;
            setIsLoading(false);
        }
    }

    async function validateDniResponse(
        valid: OrcValidations,
        values: IdentificationServiceRequest
    ) {
        if (valid && valid.success) {
            if (values.dniFront) {
                await saveDni(values.dniFront, Constants.DOC_DNI_FRONT_PR);
            }
            if (values.dniBack) {
                await saveDni(values.dniBack, Constants.DOC_DNI_BACK_PR);
            }
            enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                variant: SnackbarVariants.SUCCESS,
            });
            goToNextView();
        } else {
            setFields([...fields]);
            errorDetail.data.errors = valid.errors;
            showAlertError(errorDetail, '');
            setIsLoading(false);
        }
    }

    async function validateDni(values: IdentificationServiceRequest): Promise<OrcValidations> {
        const validation: OrcValidations = {
            success: false,
            errors: [],
        };
        if (values && values.dniBack && values.dniFront) {
            const dniBack = values.dniBack.replace(IMAGE_PREFIX, '').replace(IMAGE_PREFIX_PNG, '');
            const dniFront = values.dniFront.replace(IMAGE_PREFIX, '').replace(IMAGE_PREFIX_PNG, '');
            const request: Validations.ValidationsINERequest = {
                idReverso: dniBack,
                id: dniFront,
            };
            try {
                if (values.identificationType === INE_ID) {
                    const validationResponse: EfeResponse<any> =
                        await ValidationsService.validateDni(request);
                    validation.response = validationResponse?.data;
                    if (realOwnerData && validationResponse && validationResponse.data) {
                        const ocrValidationsResponse = validateOcrResponse(
                            validationResponse.data,
                            realOwnerData
                        );
                        return ocrValidationsResponse;
                    }
                }
                validation.success = true;
                return validation;
            } catch (error: any) {
                if (error.code === 400) {
                    validation.errors = [Messages.DOCUMENT_WAS_NOT_IDENTIFIED];
                }
                return validation;
            }
        } else {
            return validation;
        }
    }

    async function onSubmit(values: IdentificationServiceRequest) {
        try {
            const request = {...values};
            request.kycId = kycId;
            request.dniBack = '';
            request.dniFront = '';
            setIsLoading(true);
            setFields([...fields]);
            console.log('values',values);
            const response: EfeResponse<IdentificationServiceRequest> =
                await ClientService.saveIdentificationData(request);
            if (response && response.data) {
                if (!tempRealOwner?.foreignCitizen && values.identificationType === '1') {
                    await validateDniResponse(await validateDni(values), values);
                } else {
                    await saveDni(values.dniFront, Constants.DOC_DNI_FRONT_PR);
                    enqueueSnackbar(Messages.INFORMATION_SAVED_SUCCESSFULLY, {
                        variant: SnackbarVariants.SUCCESS,
                    });
                    setIsLoading(false);
                    goToNextView();
                }
            } else {
                setIsLoading(false);
                setFields([...fields]);
            }
        } catch (error) {
            setIsLoading(false);
            setFields([...fields]);
            showAlertError(error, Messages.ERROR_SAVING_INFORMATION);
        }
    }

    async function getData() {
        try {
            setIsLoading(true);
            const realownerData: EfeResponse<RealOwnerServiceRequest> =
                await ClientService.getRealOwnerData(kycId);
            if (
                !realownerData ||
                (!realownerData.data?.curp && !realownerData.data?.foreignCitizen)
            ) {
                history.push(HistoryRoutes.REAL_OWNER_PERSONAL_INFO);
                enqueueSnackbar(Messages.REQUIRED_GENERAL_DATA_SECTION, {
                    variant: SnackbarVariants.ERROR,
                });
            }
            setRealOwnerData(realownerData.data);
            const response: EfeResponse<IdentificationServiceRequest> =
                await ClientService.getIdentificationData(kycId);
            if (response && response.data) {
                response.data.dniFront = await getIdentification(
                    expedientId,
                    kycId,
                    Constants.DOC_DNI_FRONT_PR
                );
                response.data.dniBack = await getIdentification(
                    expedientId,
                    kycId,
                    Constants.DOC_DNI_BACK_PR
                );
                setRealOwnerIdentification(response.data);
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            setTempRealOwner(undefined);
        }
    }



    React.useEffect(() => {
        if (
            fatherKycId > 0 &&
            kycId > 0 &&
            ViewKeys.REAL_OWNER_IDENTIFICATION_INFO === flow.data.expandKey
        ) {
            getData();
        } else if (ViewKeys.REAL_OWNER_IDENTIFICATION_INFO === flow.data.expandKey && kycId > 0) {
            history.push(HistoryRoutes.REAL_OWNER_PERSONAL_INFO);
            enqueueSnackbar(Messages.REQUIRED_GENERAL_DATA_SECTION, {
                variant: SnackbarVariants.ERROR,
            });
        }
    }, []);

    const accordion: Array<EfeAccordionValues> = [
        {
            icon: <ActionIcon />,
            title: 'Identificaci\u00F3n del Propietario Real',
            reactElement: (
                <Box mt={8}>
                    {!isLoading && (
                        <RealOwnerIdentificationForm
                            viewId={flow.data.viewId}
                            submitHandler={onSubmit}
                            viewKey={flow.data.id}
                            foreign={tempRealOwner?.foreignCitizen || false}
                            fields={[
                                ...fieldCatalogGetter(
                                    fields.filter((field) => ATTR_NULL !== field.attrName)
                                ),
                            ].sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1))}
                            realOwnerIdentification={realOwnerIdentification}
                        />
                    )}
                </Box>
            ),
            expandIcon: true,
            disabled: false,
            expanded: ViewKeys.REAL_OWNER_IDENTIFICATION_INFO === flow.data.expandKey,
        },
    ];

    return (
        <MainContent>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <EfeAccordion onChange={onChangeHandler} values={accordion} />
        </MainContent>
    );
}

export default RealOwnerIdentification;
